import { api } from '@/lib/api';
import { changeEmail, changePassword, signOut } from '@/lib/auth-client';
import { useAuthConfig } from '@/lib/auth-config';
import { Badge, Button, Card, CardContent, Input, Label, Skeleton } from '@iq/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { LogOut, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SessionRow {
  id: string;
  isCurrent: boolean;
  createdAt: string;
  expiresAt: string;
  ipAddress?: string | null;
  userAgent?: string | null;
}

const FMT = new Intl.DateTimeFormat('en', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

function shortenUA(ua?: string | null): string {
  if (!ua) return 'Unknown device';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  return ua.split(' ')[0] ?? 'Unknown';
}

export function SecurityPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Under Authentik SSO there is no local `credential` account, so
  // change-password / change-email would fail. Identity is owned by the IdP;
  // hide both cards. Sessions + sign-out stay (they work for OIDC sessions too).
  const authConfig = useAuthConfig();
  const ssoEnabled = authConfig.data?.ssoEnabled ?? false;

  const sessionsQuery = useQuery({
    queryKey: ['account', 'sessions'],
    queryFn: async () => {
      const { data, error } = await api.GET('/account/sessions');
      if (error) throw error;
      return data.items as SessionRow[];
    },
  });

  const revokeOne = useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await api.DELETE('/account/sessions/{sessionId}', {
        params: { path: { sessionId } },
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account', 'sessions'] }),
  });

  const revokeAll = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/account/sessions/revoke-all');
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account', 'sessions'] }),
  });

  const signOutAll = useMutation({
    mutationFn: async () => {
      await revokeAll.mutateAsync();
      await signOut();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
      navigate({ to: '/signin' });
    },
  });

  return (
    <div className="flex flex-col gap-4" data-testid="settings-panel-security">
      <SessionsCard
        sessions={sessionsQuery.data ?? null}
        loading={sessionsQuery.isPending}
        onRevoke={(id) => revokeOne.mutate(id)}
        revokeBusy={revokeOne.isPending}
      />
      {ssoEnabled ? null : (
        <>
          <ChangePasswordCard />
          <ChangeEmailCard />
        </>
      )}
      <Card>
        <CardContent className="flex flex-col gap-3 py-6">
          <Label className="text-sm font-medium">{t('settings.security.signOutAll')}</Label>
          <p className="text-xs text-muted-foreground">{t('settings.security.signOutAllHint')}</p>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => signOutAll.mutate()}
              disabled={signOutAll.isPending}
              data-testid="settings-signout-all"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {signOutAll.isPending ? t('auth.signOut.pending') : t('settings.security.signOutAll')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SessionsCard({
  sessions,
  loading,
  onRevoke,
  revokeBusy,
}: {
  sessions: SessionRow[] | null;
  loading: boolean;
  onRevoke: (id: string) => void;
  revokeBusy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-6">
        <div>
          <Label className="text-sm font-medium">{t('settings.security.sessions')}</Label>
          <p className="text-xs text-muted-foreground">{t('settings.security.sessionsHint')}</p>
        </div>
        {loading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : !sessions || sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('settings.security.noSessions')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {sessions.map((s) => (
              <li
                key={s.id}
                className="flex flex-col gap-2 rounded-md border bg-muted/30 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                data-testid={`session-row-${s.id}`}
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    <span className="truncate">{shortenUA(s.userAgent)}</span>
                    {s.isCurrent ? (
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-600">
                        {t('settings.security.thisDevice')}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="break-words text-xs text-muted-foreground">
                    {s.ipAddress ? `${s.ipAddress} · ` : ''}
                    {t('settings.security.created')}: {FMT.format(new Date(s.createdAt))}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRevoke(s.id)}
                  disabled={s.isCurrent || revokeBusy}
                  data-testid={`session-revoke-${s.id}`}
                  className="self-end sm:self-auto"
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                  {t('settings.security.revoke')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ChangePasswordCard() {
  const { t } = useTranslation();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus(null);
    setPending(true);
    const result = await changePassword({ currentPassword, newPassword });
    setPending(false);
    if (result.ok) {
      setCurrent('');
      setNew('');
      setStatus({ ok: true, msg: t('settings.security.passwordChanged') });
    } else {
      setStatus({ ok: false, msg: result.message });
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-6">
        <Label className="text-sm font-medium">{t('settings.security.changePassword')}</Label>
        <p className="text-xs text-muted-foreground">{t('settings.security.changePasswordHint')}</p>
        <form className="flex flex-col gap-2" onSubmit={onSubmit}>
          <Input
            type="password"
            placeholder={t('settings.security.currentPassword')}
            value={currentPassword}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
            data-testid="settings-current-password"
          />
          <Input
            type="password"
            placeholder={t('settings.security.newPassword')}
            value={newPassword}
            onChange={(e) => setNew(e.target.value)}
            required
            minLength={12}
            autoComplete="new-password"
            data-testid="settings-new-password"
          />
          <div className="flex items-center justify-between gap-3">
            {status ? (
              <span className={status.ok ? 'text-xs text-emerald-600' : 'text-xs text-destructive'}>
                {status.msg}
              </span>
            ) : (
              <span />
            )}
            <Button
              type="submit"
              size="sm"
              disabled={pending || currentPassword.length === 0 || newPassword.length < 12}
              data-testid="settings-save-password"
            >
              {pending ? t('settings.security.saving') : t('settings.security.savePassword')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function ChangeEmailCard() {
  const { t } = useTranslation();
  const [newEmail, setEmail] = useState('');
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus(null);
    setPending(true);
    const result = await changeEmail({ newEmail });
    setPending(false);
    if (result.ok) {
      setEmail('');
      setStatus({ ok: true, msg: t('settings.security.emailQueued') });
    } else {
      setStatus({ ok: false, msg: result.message });
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-6">
        <Label className="text-sm font-medium">{t('settings.security.changeEmail')}</Label>
        <p className="text-xs text-muted-foreground">{t('settings.security.changeEmailHint')}</p>
        <form className="flex flex-col gap-2" onSubmit={onSubmit}>
          <Input
            type="email"
            placeholder={t('settings.security.newEmail')}
            value={newEmail}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            data-testid="settings-new-email"
          />
          <div className="flex items-center justify-between gap-3">
            {status ? (
              <span className={status.ok ? 'text-xs text-emerald-600' : 'text-xs text-destructive'}>
                {status.msg}
              </span>
            ) : (
              <span />
            )}
            <Button
              type="submit"
              size="sm"
              disabled={pending || newEmail.length === 0}
              data-testid="settings-save-email"
            >
              {pending ? t('settings.security.saving') : t('settings.security.saveEmail')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
